import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { IJobValuesViewModel } from 'viewModels';
import { IBrazilianStates } from 'entities';
import { useQuery } from 'react-query';
import { useAuth } from './auth';
import { UserController } from '../controller/UserController';
import { TypeUser } from '../types/enums';

type ProviderProps = {
  children: ReactNode;
};

type JobValuesProps = {
  jobValuesByUser: IJobValuesViewModel | undefined;
  setJobValuesByUser: React.Dispatch<React.SetStateAction<IJobValuesViewModel | undefined>>;
  newJobValuesAfterFetchNewSpecialties: IJobValuesViewModel | undefined;
  setNewJobValuesAfterFetchNewSpecialties: React.Dispatch<
    React.SetStateAction<IJobValuesViewModel | undefined>
  >;
  stateSelected: IBrazilianStates | undefined;
  setStateSelected: React.Dispatch<React.SetStateAction<IBrazilianStates | undefined>>;
  isLoadingJobValues: boolean;
};

const JobValuesContext = createContext({} as JobValuesProps);

const JobValuesProvider = ({ children }: ProviderProps) => {
  const { tokenIsValid, user } = useAuth();
  const [stateSelected, setStateSelected] = useState<IBrazilianStates>();
  const [jobValuesByUser, setJobValuesByUser] = useState<IJobValuesViewModel>();
  const [newJobValuesAfterFetchNewSpecialties, setNewJobValuesAfterFetchNewSpecialties] =
    useState<IJobValuesViewModel>();

  const { isLoading: isLoadingJobValues } = useQuery(
    'jobValuesByUser',
    () =>
      UserController.getJobValuesByUserId(
        user.type === TypeUser.COLLABORATOR ? (user?.companyId as string) : user.id,
      ),
    {
      onSuccess: (data) => {
        setJobValuesByUser(data);
      },
      refetchOnWindowFocus: false,
      enabled:
        !!tokenIsValid && (user.type === TypeUser.COLLABORATOR ? !!user?.companyId : !!user.id),
    },
  );

  useEffect(() => {
    if (!tokenIsValid) {
      setJobValuesByUser(undefined);
      setStateSelected(undefined);
      setNewJobValuesAfterFetchNewSpecialties(undefined);
    }
  }, [tokenIsValid]);

  const contextValues = useMemo(
    () => ({
      jobValuesByUser,
      setJobValuesByUser,
      newJobValuesAfterFetchNewSpecialties,
      setNewJobValuesAfterFetchNewSpecialties,
      isLoadingJobValues,
      stateSelected,
      setStateSelected,
    }),
    [isLoadingJobValues, jobValuesByUser, newJobValuesAfterFetchNewSpecialties, stateSelected],
  );

  return <JobValuesContext.Provider value={contextValues}>{children}</JobValuesContext.Provider>;
};

export const useJobValues = () => useContext(JobValuesContext);
export default JobValuesProvider;
